// Paylaşılan filtre+arama+pagination tablo pattern'i — §4.3 + §4.7.1.
// ClientsList, ProjectsList, UsersList bunu kullanır. Generic T için column def
// + rowKey + filters + onChange (URL debounced fetch'i dışarıda yönetilir).

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { ClientPickerItem } from "../../lib/client-picker";
import { ClientPicker } from "./ClientPicker";
import { EmptyState, ErrorState, LoadingState } from "./StateSurface";

export interface Column<T> {
  key: string;
  label: string;
  width?: string;
  align?: "left" | "right" | "center";
  render: (row: T) => ReactNode;
}

export interface FilterDropdown {
  key: string;
  /** Native select (varsayılan). */
  kind?: "select" | undefined;
  label: string; // placeholder
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}

/**
 * Müşteri facet'i (ClientPicker işi, 2026-10-07): `GET /clients?limit=200` ile doldurulan
 * native select yerine sunucu aramalı `ClientPicker` (inline, boş bırakılabilir). Diğer
 * facet'ler native select kalır.
 */
export interface ClientFilter {
  key: string;
  kind: "client";
  /** Erişilebilir ad + placeholder (ör. "Müşteri (tümü)"). */
  label: string;
  value: ClientPickerItem | null;
  onChange: (v: ClientPickerItem | null) => void;
}

export type TableFilter = FilterDropdown | ClientFilter;

interface Props<T> {
  items: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;
  filters?: TableFilter[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  emptyHint?: string;
  /** Boş durumda ipucunun altında görünen aksiyon (ör. "Filtreleri temizle"). */
  emptyAction?: ReactNode;
  errorMessage?: string | null;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  rightActions?: ReactNode;
  rowHighlight?: (row: T) => boolean;
  /**
   * Geçmiş Rapor Aktarımı (tasarım review 1A, B12): çok kolonlu tabloyu YALNIZ
   * `<table>` çevresindeki iç sarmalayıcıda yatay kaydırılabilir yapar. Dış
   * `rounded-xl overflow-hidden` kabuk + filtre barı + pagination aynen kalır,
   * mevcut çağıranlar (ClientsList/ProjectsList/UsersList/ArchiveList) byte-eşit.
   */
  scrollX?: boolean;
  /** `scrollX` ile birlikte tablonun alt sınır genişliği (px). */
  minTableWidth?: number;
}

export function FilteredTable<T>({
  items,
  columns,
  rowKey,
  search,
  onSearchChange,
  searchPlaceholder = "Ara…",
  filters = [],
  total,
  page,
  pageSize,
  onPageChange,
  loading,
  emptyMessage = "Kayıt bulunamadı.",
  emptyHint,
  emptyAction,
  errorMessage,
  onRetry,
  onRowClick,
  rightActions,
  rowHighlight,
  scrollX = false,
  minTableWidth,
}: Props<T>): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(total, page * pageSize);
  const pageNumbers = useMemo(() => {
    const nums: number[] = [];
    const max = Math.min(totalPages, 5);
    let start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + max - 1);
    start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }, [page, totalPages]);

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-sm)",
      }}
    >
      {/* Filter bar */}
      <div
        style={{
          padding: "12px 14px",
          borderBottom: "1px solid var(--color-border)",
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          background: "var(--color-bg)",
        }}
      >
        <input
          type="text"
          aria-label={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          style={{
            flex: "1 1 240px",
            minWidth: 220,
            padding: "7px 12px",
            borderRadius: 6,
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            fontSize: "0.85rem",
            color: "var(--color-text)",
          }}
        />
        {filters.map((f) =>
          f.kind === "client" ? (
            <ClientPicker
              key={f.key}
              inline
              size="sm"
              allowEmpty
              ariaLabel={f.label}
              placeholder={f.label}
              value={f.value}
              onChange={f.onChange}
            />
          ) : (
            <select
              key={f.key}
              aria-label={f.label}
              value={f.value}
              onChange={(e) => f.onChange(e.target.value)}
              style={{
                padding: "7px 10px",
                borderRadius: 6,
                border: "1px solid var(--color-border)",
                background: "var(--color-surface)",
                fontSize: "0.85rem",
                color: "var(--color-text)",
                minWidth: 140,
              }}
            >
              <option value="">{f.label}</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ),
        )}
        {rightActions ? (
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>{rightActions}</div>
        ) : null}
      </div>

      {/* Table */}
      {errorMessage ? (
        <ErrorState message={errorMessage} {...(onRetry ? { onRetry } : {})} />
      ) : loading && items.length === 0 ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState
          title={emptyMessage}
          {...(emptyHint ? { hint: emptyHint } : {})}
          {...(emptyAction ? { action: emptyAction } : {})}
        />
      ) : (
        <TableScroll enabled={scrollX}>
          <table
            style={{
              width: "100%",
              fontSize: "0.88rem",
              ...(minTableWidth !== undefined ? { minWidth: minTableWidth } : {}),
            }}
          >
            <thead>
              <tr
                style={{
                  background: "var(--color-bg)",
                  color: "var(--color-text-3)",
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {columns.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      textAlign: c.align ?? "left",
                      padding: "10px 14px",
                      fontWeight: 600,
                      width: c.width,
                    }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{
                    borderTop: "1px solid var(--color-border)",
                    cursor: onRowClick ? "pointer" : "default",
                    opacity: rowHighlight?.(row) === false ? 0.55 : 1,
                    transition: "background 80ms ease",
                  }}
                  onMouseEnter={(e) => {
                    if (onRowClick) e.currentTarget.style.background = "rgba(15,42,61,0.03)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        padding: "10px 14px",
                        textAlign: c.align ?? "left",
                        color: "var(--color-text)",
                      }}
                      onClick={(e) => {
                        // row click tetiklenmesini engellemek için kolonları
                        // render içinde event stop yapabilir — global bir kural değil.
                        void e;
                      }}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {/* Pagination footer */}
      {total > 0 ? (
        <div
          style={{
            padding: "10px 14px",
            borderTop: "1px solid var(--color-border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "0.78rem",
            color: "var(--color-text-3)",
            background: "var(--color-bg)",
          }}
        >
          <span>
            <strong style={{ color: "var(--color-text-2)" }}>
              {pageStart}–{pageEnd}
            </strong>{" "}
            / {total}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <PageBtn
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
              label="←"
            />
            {pageNumbers.map((n) => (
              <PageBtn
                key={n}
                disabled={loading}
                onClick={() => onPageChange(n)}
                label={String(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              disabled={page >= totalPages || loading}
              onClick={() => onPageChange(page + 1)}
              label="→"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * B12 — `scrollX` kapalıyken HİÇBİR ek DOM düğümü üretmez (mevcut çağıranlar
 * birebir aynı ağacı render eder); açıkken yalnız `<table>`'ı saran iç kaydırma
 * kabı eklenir, dış `rounded-xl overflow-hidden` kabuk değişmez.
 */
function TableScroll({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}): JSX.Element {
  if (!enabled) return <>{children}</>;
  return <div style={{ overflowX: "auto" }}>{children}</div>;
}

function PageBtn({
  disabled,
  onClick,
  label,
  active,
}: {
  disabled?: boolean | undefined;
  onClick: () => void;
  label: string;
  active?: boolean | undefined;
}): JSX.Element {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: 28,
        padding: "3px 8px",
        borderRadius: 4,
        border: "1px solid var(--color-border)",
        background: active ? "var(--color-primary)" : "var(--color-surface)",
        color: active ? "var(--color-surface)" : "var(--color-text-2)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        fontSize: "0.78rem",
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

// Debounce helper — arama input'ı için (350ms default).
export function useDebouncedValue<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
